// /pack/:packId/item/:itemId — detail page for one item, refactored in
// round-1 to a 2-tab layout:
//
//   A 影片故事  YouTube embed (when PACKS.itemDetails[itemId].videoUrl is set)
//             + per-item description text
//   B 彩繪填圖  Existing lineart / user-artwork hero + 上色 / 繼續上色 CTA
//
// First-visit default = tab A (kid hasn't seen it before — surprise them with
// the story). Subsequent visits default = tab B (kid wants to paint, don't
// force them through the story every time). "First visit" is tracked
// server-side via user_items.detail_seen_at and surfaced through the
// pack-scene query's SceneItem.detailSeenAt; on mount we fire-and-forget a
// PATCH /api/items/:itemId/detail-seen so the second visit gets the new
// default. Manual tab switch is always allowed and is NOT persisted.
//
// Reuses the pack-scene query (same staleTime as PackHomePage / Inventory).
// Locked items (!isUnlocked) bounce back to the pack home — info button
// entries only show on unlocked cells, but this guard catches direct URLs.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  assetUrl,
  isPackId,
  PACKS,
  type PackId,
  type SceneItem,
} from '@skena/schema'
import { fetchPackScene } from '../scene/sceneApi.ts'
import { ShareButton } from '../components/ShareButton.tsx'
import { loadCorsImage } from '../lib/share-card-canvas.ts'
import { useAuthStore } from '../auth/store.ts'

const PACK_BG: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-bg)]',
  nativity: 'bg-[var(--color-nativity-bg)]',
}
const PACK_ACCENT_TEXT: Record<PackId, string> = {
  joseph: 'text-[var(--color-joseph-primary)]',
  nativity: 'text-[var(--color-nativity-primary)]',
}
const PACK_OUTLINE_BUTTON: Record<PackId, string> = {
  joseph:
    'border-[var(--color-joseph-primary)] text-[var(--color-joseph-primary)]',
  nativity:
    'border-[var(--color-nativity-primary)] text-[var(--color-nativity-primary)]',
}
const PACK_SOLID_BUTTON: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-primary)] text-white',
  nativity: 'bg-[var(--color-nativity-primary)] text-white',
}

type TabId = 'story' | 'color'

const IFRAME_LOAD_TIMEOUT_MS = 5000

/**
 * youtu.be/<id> or youtube.com/watch?v=<id> → /embed/<id>. Returns null when
 * the URL doesn't match a known YouTube shape; tab A then shows the
 * placeholder. We accept any valid YouTube watch URL but only support the
 * two common forms — round-1 only ships one URL and we control the format.
 */
function toYoutubeEmbed(url: string): string | null {
  try {
    const u = new URL(url)
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '')
      return id ? `https://www.youtube.com/embed/${id}` : null
    }
    if (u.hostname.endsWith('youtube.com')) {
      const id = u.searchParams.get('v')
      return id ? `https://www.youtube.com/embed/${id}` : null
    }
  } catch {
    /* not a valid URL */
  }
  return null
}

async function markDetailSeen(itemId: string): Promise<void> {
  try {
    await fetch(`/api/items/${encodeURIComponent(itemId)}/detail-seen`, {
      method: 'PATCH',
      credentials: 'include',
    })
  } catch (err) {
    // Fire-and-forget. PATCH failure means next visit will retry; the kid
    // sees no error UI. Log for dev debugging only.
    console.warn('[detail] markDetailSeen failed', err)
  }
}

export function ItemDetailPage() {
  const { packId, itemId } = useParams<{ packId: string; itemId: string }>()
  if (!packId || !isPackId(packId) || !itemId) {
    return <Navigate to="/" replace />
  }
  return <ItemDetailInner packId={packId} itemId={itemId} />
}

function ItemDetailInner({ packId, itemId }: { packId: PackId; itemId: string }) {
  const pack = PACKS[packId]
  const user = useAuthStore((s) => s.user)
  const sceneQuery = useQuery({
    queryKey: ['pack-scene', packId],
    queryFn: () => fetchPackScene(packId),
    staleTime: 30 * 1000,
  })

  if (sceneQuery.isLoading) {
    return (
      <main
        className={`mx-auto flex min-h-full max-w-2xl flex-col gap-6 p-6 ${PACK_BG[packId]}`}
        data-pack={packId}
      >
        <p className="text-sm text-[var(--color-muted)]">準備中…</p>
      </main>
    )
  }

  if (sceneQuery.isError) {
    return (
      <main
        className={`mx-auto flex min-h-full max-w-2xl flex-col gap-6 p-6 ${PACK_BG[packId]}`}
        data-pack={packId}
      >
        <header>
          <Link
            to={`/pack/${packId}`}
            className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
          >
            ← 回 {pack.nameZh}
          </Link>
        </header>
        <p className="text-sm text-[var(--color-muted)]">載入失敗，等等再回來</p>
      </main>
    )
  }

  const items = sceneQuery.data?.items ?? []
  const item = items.find((i) => i.itemId === itemId)

  if (!item || !item.isUnlocked) {
    return <Navigate to={`/pack/${packId}`} replace />
  }

  return <ItemDetailReady packId={packId} item={item} userDisplayName={user?.displayName ?? '小朋友'} churchName={user?.churchName ?? null} />
}

function ItemDetailReady({
  packId,
  item,
  userDisplayName,
  churchName,
}: {
  packId: PackId
  item: SceneItem
  userDisplayName: string
  churchName: string | null
}) {
  const pack = PACKS[packId]
  // PACKS.itemDetails has a literal-key object per pack (TS infers narrow
  // string-literal keys, ideal for compile-time exhaustive checks). Indexing
  // by SceneItem.itemId (typed as `string`) needs a widening cast — keys
  // and values are guaranteed by packs.test.ts runtime exhaustive check.
  const detail = (pack.itemDetails as Record<string, { videoUrl: string | null }>)[
    item.itemId
  ]
  const videoUrl = detail?.videoUrl ?? null
  const embedUrl = videoUrl ? toYoutubeEmbed(videoUrl) : null
  // First visit (detail_seen_at IS NULL) → tab A; subsequent visits → tab B.
  const initialTab: TabId = item.detailSeenAt === null ? 'story' : 'color'
  const [tab, setTab] = useState<TabId>(initialTab)

  // Fire PATCH once on mount when the user hasn't seen this page yet. Local
  // tab switches do NOT re-fire — we only want to record the FIRST visit.
  useEffect(() => {
    if (item.detailSeenAt !== null) return
    void markDetailSeen(item.itemId)
    // We intentionally omit item.detailSeenAt from the dep array — if the
    // pack-scene query refetches and surfaces a new value, we don't want
    // a duplicate PATCH.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.itemId])

  const switchToColor = useCallback(() => setTab('color'), [])
  const switchToStory = useCallback(() => setTab('story'), [])

  return (
    <main
      className={`mx-auto flex min-h-full max-w-2xl flex-col gap-6 p-6 ${PACK_BG[packId]}`}
      data-pack={packId}
    >
      <header className="flex items-baseline justify-between gap-4">
        <Link
          to={`/pack/${packId}`}
          className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
        >
          ← 回 {pack.nameZh}
        </Link>
        <p
          className={`text-xs tracking-wider uppercase ${PACK_ACCENT_TEXT[packId]}`}
        >
          {item.scriptureRef}
        </p>
      </header>

      <section className="flex flex-col items-center gap-1 text-center">
        <h1 className="font-[family-name:var(--font-display)] text-3xl text-[var(--color-fg)]">
          {item.nameZh}
        </h1>
        <p className="text-sm text-[var(--color-muted)]">{item.nameEn}</p>
      </section>

      <DetailTabs tab={tab} onPickStory={switchToStory} onPickColor={switchToColor} />

      {tab === 'story' ? (
        <StoryTab
          packId={packId}
          item={item}
          embedUrl={embedUrl}
          onSwitchToColor={switchToColor}
        />
      ) : (
        <ColorTab
          packId={packId}
          item={item}
          userDisplayName={userDisplayName}
          churchName={churchName}
        />
      )}
    </main>
  )
}

function DetailTabs({
  tab,
  onPickStory,
  onPickColor,
}: {
  tab: TabId
  onPickStory: () => void
  onPickColor: () => void
}) {
  const base =
    'min-h-[44px] flex-1 rounded-[var(--radius-pill)] px-5 py-2 text-sm font-medium transition-colors'
  return (
    <div role="tablist" aria-label="頁面切換" className="flex items-center gap-2">
      <button
        role="tab"
        aria-selected={tab === 'story'}
        onClick={onPickStory}
        className={`${base} ${
          tab === 'story'
            ? 'bg-[var(--color-fg)] text-white'
            : 'border border-[var(--color-muted)]/30 text-[var(--color-muted)]'
        }`}
      >
        影片故事
      </button>
      <button
        role="tab"
        aria-selected={tab === 'color'}
        onClick={onPickColor}
        className={`${base} ${
          tab === 'color'
            ? 'bg-[var(--color-fg)] text-white'
            : 'border border-[var(--color-muted)]/30 text-[var(--color-muted)]'
        }`}
      >
        彩繪填圖
      </button>
    </div>
  )
}

function StoryTab({
  packId,
  item,
  embedUrl,
  onSwitchToColor,
}: {
  packId: PackId
  item: SceneItem
  embedUrl: string | null
  onSwitchToColor: () => void
}) {
  // navigator.onLine is the cheap pre-check; not authoritative on iOS Safari.
  const initialOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
  const [iframeFailed, setIframeFailed] = useState(false)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [timedOut, setTimedOut] = useState(false)
  const [offline, setOffline] = useState(!initialOnline)

  // Listen for online/offline transitions while the tab is mounted so kid
  // doesn't have to refresh to recover.
  useEffect(() => {
    const onOnline = () => setOffline(false)
    const onOffline = () => setOffline(true)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  // Iframe load timeout — if onload never fires within IFRAME_LOAD_TIMEOUT_MS,
  // we treat it as failed (most likely YT blocked, or very slow network).
  useEffect(() => {
    if (!embedUrl || iframeLoaded || iframeFailed) return
    const t = window.setTimeout(() => setTimedOut(true), IFRAME_LOAD_TIMEOUT_MS)
    return () => window.clearTimeout(t)
  }, [embedUrl, iframeLoaded, iframeFailed])

  const showFallback =
    offline || iframeFailed || timedOut || embedUrl === null

  return (
    <div className="flex flex-col gap-5">
      <div className="aspect-video w-full overflow-hidden rounded-[var(--radius-soft)] bg-white shadow-[var(--shadow-card)]">
        {showFallback ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-white p-6 text-center">
            <p className="text-sm text-[var(--color-muted)]">
              {embedUrl === null
                ? '故事影片製作中'
                : offline
                  ? '需要網路觀看故事'
                  : '影片載入失敗'}
            </p>
            <button
              onClick={onSwitchToColor}
              className={`rounded-[var(--radius-pill)] px-5 py-2 text-sm font-medium ${PACK_SOLID_BUTTON[packId]}`}
            >
              改去彩繪 →
            </button>
          </div>
        ) : (
          <iframe
            key={embedUrl}
            src={embedUrl ?? ''}
            title={`${item.nameZh} 故事影片`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="block h-full w-full"
            onLoad={() => setIframeLoaded(true)}
            onError={() => setIframeFailed(true)}
          />
        )}
      </div>

      {item.descriptionZh ? (
        <section
          className="flex flex-col gap-3 rounded-[var(--radius-soft)] border border-black/5 bg-white/70 p-5"
          aria-label="元素說明"
        >
          <p className="text-base leading-relaxed text-[var(--color-fg)]">
            {item.descriptionZh}
          </p>
          {item.descriptionEn ? (
            <p className="text-sm italic leading-relaxed text-[var(--color-muted)]">
              {item.descriptionEn}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  )
}

function ColorTab({
  packId,
  item,
  userDisplayName,
  churchName,
}: {
  packId: PackId
  item: SceneItem
  userDisplayName: string
  churchName: string | null
}) {
  const isUserArtwork = item.isComplete && Boolean(item.artworkUrl)
  const heroUrl = isUserArtwork
    ? (item.artworkUrl as string)
    : assetUrl(packId, item.itemId, 'lineart')

  const heroLoader = useMemo(
    () => () => loadCorsImage(item.artworkUrl as string),
    [item.artworkUrl],
  )

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col items-center">
        <div className="w-full overflow-hidden rounded-[var(--radius-soft)] bg-white shadow-[var(--shadow-card)]">
          <img
            src={heroUrl}
            alt={item.nameZh}
            {...(isUserArtwork ? { crossOrigin: 'anonymous' as const } : {})}
            className="block h-auto w-full"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>
      </section>

      <div className="flex flex-col gap-3">
        {item.isComplete && item.artworkUrl && item.artworkId ? (
          <ShareButton
            artworkId={item.artworkId}
            getHero={heroLoader}
            itemNameZh={item.nameZh}
            itemNameEn={item.nameEn}
            scriptureRef={item.scriptureRef}
            descriptionZh={item.descriptionZh}
            displayName={userDisplayName}
            churchName={churchName}
            packId={packId}
            variant="primary"
          />
        ) : null}
        <Link
          to={`/pack/${packId}/color/${item.itemId}`}
          className={`rounded-[var(--radius-pill)] border-2 px-6 py-3 text-center text-base font-semibold transition-transform hover:-translate-y-0.5 ${PACK_OUTLINE_BUTTON[packId]}`}
        >
          {item.isComplete ? '繼續上色' : '來上色'}
        </Link>
      </div>
    </div>
  )
}
